<template>
  <v-dialog
    v-model="open"
    max-width="760"
    scrollable
  >
    <v-card class="photo-animation-dialog">
      <v-card-title class="d-flex justify-space-between">
        Animate photo
        <v-btn
          icon
          aria-label="Close photo animation"
          @click="open = false"
        >
          <v-icon>{{ mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-5">
        <img
          :src="photoUrl"
          alt="Photo to animate"
          class="photo-animation-preview"
        />
        <p class="mt-4">
          Bring this moment to life. We analyze your photo and create natural movement that fits the scene.
        </p>
        <v-progress-linear
          v-if="loading"
          indeterminate
          color="primary"
          aria-label="Loading animation models"
        />
        <v-alert
          v-if="error"
          type="error"
          dense
          outlined
        >
          {{ error }}
        </v-alert>
        <v-btn
          v-if="error && !models.length && !loading"
          text
          color="primary"
          @click="load"
        >
          Try again
        </v-btn>
        <v-radio-group
          v-if="models.length"
          v-model="model"
          :disabled="starting || hasActiveJob"
          aria-label="Video model"
        >
          <v-radio
            v-for="option in models"
            :key="option.id"
            :value="option.id"
            class="photo-animation-model"
          >
            <template v-slot:label>
              <div>
                <strong>{{ option.name }}</strong><br />
                <span>{{ option.duration }} seconds · {{ option.resolution }} · up to {{ option.maxCredits }} credits</span>
              </div>
            </template>
          </v-radio>
        </v-radio-group>
        <p
          v-if="models.length"
          class="text-caption"
        >
          Photo analysis is included when needed. Your original photo is preserved.
        </p>
        <v-btn
          v-if="selectedModel"
          block
          color="primary"
          :loading="starting"
          :disabled="loading || hasActiveJob"
          @click="generate()"
        >
          Generate video · up to {{ selectedModel.maxCredits }} credits
        </v-btn>
        <p
          v-if="hasActiveJob"
          class="mt-3 mb-0"
        >
          You can close this window and return to this photo later.
        </p>
        <section
          v-if="jobs.length"
          class="mt-6"
          aria-label="Photo animations"
        >
          <h3 class="text-subtitle-1 mb-3">
            Your animations
          </h3>
          <v-card
            v-for="job in jobs"
            :key="job.jobId"
            outlined
            class="mb-4 pa-3"
          >
            <div class="d-flex justify-space-between flex-wrap mb-2">
              <strong>{{ job.modelName }}</strong>
              <span>{{ job.status === 'succeeded' ? `${job.credits} credits used` : statusLabel(job) }}</span>
            </div>
            <template v-if="isActive(job)">
              <v-progress-linear
                indeterminate
                color="primary"
                :aria-label="statusLabel(job)"
              />
              <p
                class="mt-2 mb-0"
                role="status"
              >
                {{ statusLabel(job) }}
              </p>
            </template>
            <p
              v-if="job.motion"
              class="mt-2 mb-2"
            >
              {{ job.motion }}
            </p>
            <video
              v-if="job.status === 'succeeded'"
              :src="job.url"
              :poster="job.poster"
              controls
              playsinline
              preload="none"
              class="photo-animation-video"
            />
            <p
              v-if="job.failureReason"
              class="mt-2 mb-2"
              role="status"
            >
              <strong>Reason:</strong> {{ job.failureReason }}
            </p>
            <p
              v-if="job.message"
              class="mb-0"
              role="status"
            >
              {{ job.message }}
            </p>
            <v-btn
              v-if="job.status === 'failed' && job.canRetry !== false"
              outlined
              small
              color="primary"
              class="mt-3"
              :loading="retrying === job.jobId"
              :disabled="loading || starting || hasActiveJob || !retryCredits(job)"
              @click="retry(job)"
            >
              Retry · up to {{ retryCredits(job) }} credits
            </v-btn>
            <small v-if="job.status === 'review'">Request: {{ job.jobId }}</small>
          </v-card>
        </section>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer /><v-btn
          text
          @click="open = false"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mdiClose } from '@mdi/js'
import { animationOptions, animationStatus, createAnimation } from '@/services/animations'
import { aiError, creditsChanged } from '@/services/ai'

export default {
  name: 'PhotoAnimationDialog',
  props: {
    value: Boolean,
    fileLinkId: { type: [String, Number], required: true },
    sourceVersion: { type: String, default: 'original' },
    photoUrl: { type: String, default: '' },
  },
  data: () => ({
    mdiClose, models: [], jobs: [], model: 'seedance', loading: false, starting: false, retrying: null, error: '', timer: null, version: 0,
  }),
  computed: {
    open: { get() { return this.value }, set(value) { this.$emit('input', value) } },
    selectedModel() { return this.models.find(item => item.id === this.model) },
    hasActiveJob() { return this.jobs.some(job => this.isActive(job) || job.status === 'review') },
  },
  watch: {
    value(value) { if (value) this.load(); else this.stop() },
    fileLinkId() { if (this.value) this.load() },
    sourceVersion() { if (this.value) this.load() },
  },
  mounted() { if (this.value) this.load() },
  beforeDestroy() { this.stop() },
  methods: {
    isActive(job) { return ['queued', 'starting', 'processing', 'saving'].includes(job.status) },
    statusLabel(job) {
      if (job.status === 'succeeded') return 'Ready'
      if (job.status === 'failed') return 'Credits returned'
      if (job.status === 'review') return 'Needs review'
      if (job.stage === 'saving') return 'Saving your video...'

      return job.stage === 'analysis' ? 'Analyzing your photo...' : 'Generating your video...'
    },
    stop() { clearTimeout(this.timer); this.version += 1 },
    async load() {
      this.stop()
      const { version } = this
      this.loading = true
      this.error = ''
      try {
        const data = await animationOptions(this.fileLinkId, this.sourceVersion)
        if (version !== this.version) return
        this.models = data.models
        this.jobs = data.jobs
        this.jobs.filter(job => job.status === 'succeeded').forEach(job => this.$emit('generated', job))
        this.schedule()
      } catch (error) { if (version === this.version) this.error = aiError(error) } finally { if (version === this.version) this.loading = false }
    },
    schedule() {
      clearTimeout(this.timer)
      if (this.value && this.jobs.some(this.isActive)) this.timer = setTimeout(this.poll, 3000)
    },
    async poll() {
      const { version } = this
      try {
        const results = await Promise.all(this.jobs.filter(this.isActive).map(job => animationStatus(job.jobId)))
        if (version !== this.version) return
        let finished = false
        results.forEach(result => {
          const index = this.jobs.findIndex(job => job.jobId === result.jobId)
          if (index >= 0) this.$set(this.jobs, index, result)
          if (['succeeded', 'failed'].includes(result.status)) {
            finished = true
            if (result.status === 'succeeded') this.$emit('generated', result)
          }
        })
        if (finished) {
          creditsChanged()
          const data = await animationOptions(this.fileLinkId, this.sourceVersion)
          if (version !== this.version) return
          this.models = data.models
        }
        this.error = ''
      } catch (error) { if (version === this.version) this.error = aiError(error) }
      if (version === this.version) this.schedule()
    },
    retryCredits(job) {
      const option = this.models.find(item => item.id === job.model)

      return job.retryMaxCredits || (option && option.maxCredits)
    },
    retry(job) {
      if (this.loading || this.starting || this.hasActiveJob || job.status !== 'failed' || job.canRetry === false || !this.retryCredits(job)) return null
      this.model = job.model

      return this.generate({
        model: job.model,
        sourceVersion: job.sourceVersion || this.sourceVersion,
        maxCredits: this.retryCredits(job),
        retryJobId: job.jobId,
      })
    },
    async generate(options = {}) {
      if (this.loading || this.starting || this.hasActiveJob || !this.selectedModel) return
      const { version } = this
      this.starting = true
      this.retrying = options.retryJobId || null
      this.error = ''
      try {
        const job = await createAnimation({
          files_link_id: Number(this.fileLinkId),
          source_version: options.sourceVersion || this.sourceVersion,
          model: options.model || this.model,
          max_credits: options.maxCredits || this.selectedModel.maxCredits,
        }, options.retryJobId)
        if (version !== this.version) return
        if (!this.jobs.some(item => item.jobId === job.jobId)) this.jobs.unshift(job)
        this.schedule()
      } catch (error) {
        if (version === this.version) this.error = aiError(error)
      } finally { this.starting = false; this.retrying = null }
    },
  },
}
</script>

<style scoped>
.photo-animation-preview { display: block; width: 100%; max-height: 240px; object-fit: contain; border-radius: 8px; }
.photo-animation-model { padding: 12px; border: 1px solid rgba(128,128,128,.25); border-radius: 8px; align-items: center; }
.photo-animation-model span { font-size: 14px; }
.photo-animation-video { display: block; width: 100%; max-height: 360px; border-radius: 8px; }
@media (max-width: 600px) { .photo-animation-dialog >>> .v-card__text { padding: 16px; } }
</style>
